import React, { useMemo } from "react";

import { PostFieldLabel, PlatformUsage } from "components/Posts/PostForm";
import { countCharacter } from "utils/strings";
import PostFieldError from "components/Posts/PostForm/PostFieldError/PostFieldError";
import { FIELDS } from "constants/PostFormFields";
import { useDebouncedCallback } from "use-debounce";
import { useSkedCaptionAutocomplete } from "ui/SkedCaption";
import { SkedCaptionV2 } from "@/ui/SkedCaption/v2/sked-caption-v2";
import { Post } from "../../../../features/post";
import { PlatformSpec } from "../../../../constants/Platforms";
import { FormValidationErrors } from "../../../../shared/types/post";
import {
  AccountPlatformType,
  PostLinkedInAccountType,
} from "../../../../shared/types/accounts";

const PostFirstComment = ({
  post,
  selectedPlatform,
  platforms,
  onChange,
  className = "",
  errors,
  disabled = false,
  hashtagCount,
  hasMoreThanOnelinkedInAccount,
  selectedLinkedInAccountType,
  selectedLinkedUrn,
}: {
  post: Post;
  selectedPlatform: AccountPlatformType | "LINKED";
  platforms: PlatformSpec[];
  onChange: (value: string) => void;
  className?: string;
  errors?: FormValidationErrors;
  disabled?: boolean;
  hashtagCount: number;
  hasMoreThanOnelinkedInAccount?: boolean;
  selectedLinkedInAccountType?: PostLinkedInAccountType;
  selectedLinkedUrn?: string;
}) => {
  const skedCaptionAutocomplete = useSkedCaptionAutocomplete(selectedPlatform);
  const firstcomment = useMemo(() => post.firstcomment || "", [post]);
  const mentionCount = countCharacter(firstcomment, "@");

  const bounce = useDebouncedCallback((value) => {
    if (value !== firstcomment) {
      onChange(value);
    }
  }, 500);

  return (
    <div data-testid="postFirstComment" className={className}>
      <div className="flex items-center">
        <PostFieldLabel className="flex items-center mb-2">
          <div className="mr-2"> First Comment</div>
          <PlatformUsage
            platforms={platforms}
            field={FIELDS.FIRST_COMMENT}
            selectedPlatform={selectedPlatform}
          />
        </PostFieldLabel>
      </div>
      <SkedCaptionV2
        selectedPlatform={selectedPlatform}
        initialValue={firstcomment}
        disabled={disabled}
        onChange={bounce}
        suggestions={true}
        descriptionsHidden={true}
        autocomplete={skedCaptionAutocomplete}
        hasMoreThanOnelinkedInAccount={hasMoreThanOnelinkedInAccount}
        selectedLinkedInAccountType={selectedLinkedInAccountType}
        selectedLinkedUrn={selectedLinkedUrn}
        counters={{
          characters: firstcomment.length,
          charactersMax: 2200,
          hashtags: hashtagCount,
          hashtagsMax: 30,
          mentions: mentionCount > 0 ? mentionCount : undefined,
        }}
      />

      <PostFieldError
        className="mt-4"
        fields={[FIELDS.FIRST_COMMENT]}
        errors={errors}
      />
    </div>
  );
};

export default PostFirstComment;
